/**
 * Read-only view of the user's automatic follow-up routines.
 *
 * Routines are configured (toggled on/off) in the Settings route; here we only
 * surface which ones are active and when they run, since they are conceptually
 * "automatic reminders". State is read from `users/{uid}.preferences.routines`
 * via `useUserDoc`. The controls stay in Settings — this component links there.
 */
import type { RoutineType } from '@agente/shared'
import { Link } from '@tanstack/react-router'
import { CalendarClock } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useUserDoc } from '@/hooks/useUserDoc'

/**
 * Local labels for display only. Titles mirror the Settings toggles; schedules
 * describe the canonical `ROUTINE_CRON` windows in plain pt-BR. Kept as a small
 * duplicated array on purpose — Settings owns the toggle logic.
 */
const ROUTINE_INFO: Array<{ key: RoutineType; title: string; schedule: string }> = [
  { key: 'morningBriefing', title: 'Lembrete às 7h', schedule: 'Todos os dias pela manhã.' },
  { key: 'eveningBriefing', title: 'Resumo às 20h', schedule: 'Todos os dias no fim da tarde.' },
  {
    key: 'hourlyFollowup',
    title: 'Follow-up de hora em hora',
    schedule: 'Das 8h às 20h, a cada hora.',
  },
]

export function RoutineList() {
  const { userDoc, loading } = useUserDoc()
  const routines = userDoc?.preferences?.routines
  const activeCount = ROUTINE_INFO.filter(({ key }) => routines?.[key] === true).length

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarClock className="text-brand size-5 shrink-0" aria-hidden="true" />
          Rotinas automáticas
        </CardTitle>
        <CardDescription>
          Acompanhamentos recorrentes entregues no Teams. Ajuste-os em Configurações.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-12 w-full rounded-lg" />
            ))}
          </div>
        ) : activeCount === 0 ? (
          <p className="text-muted-foreground text-sm">
            Nenhuma rotina ativa. Ative em Configurações.
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {ROUTINE_INFO.map(({ key, title, schedule }) => {
              const active = routines?.[key] === true
              return (
                <li
                  key={key}
                  className="border-border bg-card flex items-start justify-between gap-4 rounded-lg border p-3"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{title}</p>
                    <p className="text-muted-foreground mt-0.5 text-xs">{schedule}</p>
                  </div>
                  <RoutineStatus active={active} />
                </li>
              )
            })}
          </ul>
        )}

        <div>
          <Button asChild variant="outline" size="sm">
            <Link to="/settings">Gerenciar em Configurações</Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/** Small non-interactive badge showing whether a routine is on or off. */
function RoutineStatus({ active }: { active: boolean }) {
  return (
    <span
      className={
        active
          ? 'bg-brand/10 text-brand inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium'
          : 'bg-muted text-muted-foreground inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium'
      }
    >
      <span
        className={
          active ? 'bg-brand size-1.5 rounded-full' : 'bg-muted-foreground size-1.5 rounded-full'
        }
        aria-hidden="true"
      />
      {active ? 'Ativa' : 'Inativa'}
    </span>
  )
}

export default RoutineList
